import React, { useEffect, useState } from "react";
import { G, useStore } from "./store";
import { api } from "./api";
import { Game } from "./Game";
import { DialogHost, confirm, prompt, readFileAsText } from "./util";

function useHash() {
  const [h, setH] = useState(location.hash);
  useEffect(() => {
    const f = () => setH(location.hash);
    window.addEventListener("hashchange", f);
    return () => window.removeEventListener("hashchange", f);
  }, []);
  return h;
}

export function App() {
  const user = useStore(G, s => s.user);
  const [checked, setChecked] = useState(false);
  const hash = useHash();

  useEffect(() => {
    api("/api/me").then(r => G.set({ user: r.user })).catch(() => {}).finally(() => setChecked(true));
  }, []);

  if (!checked) return <div className="center-screen"><div className="spinner" /></div>;
  const join = hash.match(/^#\/join\/([A-Za-z0-9]+)/);
  const game = hash.match(/^#\/g\/([A-Za-z0-9]+)/);
  return (
    <>
      {join ? <Join code={join[1]} /> : !user ? <Auth /> : game ? <Game key={game[1]} id={game[1]} /> : <Lobby />}
      <DialogHost />
    </>
  );
}

function Auth({ invite, onDone }: { invite?: string; onDone?: () => void }) {
  const [mode, setMode] = useState<"login" | "register">(invite ? "register" : "login");
  const [f, setF] = useState({ username: "", password: "", display: "", code: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api(mode === "login" ? "/api/login" : "/api/register", { body: { ...f, invite } });
      G.set({ user: r.user });
      onDone?.();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div className="center-screen auth">
      <form className="card" onSubmit={submit}>
        <h1 className="logo">🔥 Hearth</h1>
        <p className="dim">A virtual tabletop for your group.</p>
        <div className="seg-toggle wide">
          <button type="button" className={mode === "login" ? "on" : ""} onClick={() => setMode("login")}>Log in</button>
          <button type="button" className={mode === "register" ? "on" : ""} onClick={() => setMode("register")}>Create account</button>
        </div>
        <input placeholder="Username" autoComplete="username" value={f.username} onChange={e => setF({ ...f, username: e.target.value })} autoFocus />
        <input placeholder="Password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} value={f.password} onChange={e => setF({ ...f, password: e.target.value })} />
        {mode === "register" && (
          <>
            <input placeholder="Display name (what others see)" value={f.display} onChange={e => setF({ ...f, display: e.target.value })} />
            {!invite && <input placeholder="Signup code (ask the site owner)" value={f.code} onChange={e => setF({ ...f, code: e.target.value })} />}
          </>
        )}
        {err && <div className="err">{err}</div>}
        <button className="primary big" disabled={busy}>{mode === "login" ? "Log in" : "Create account"}</button>
      </form>
    </div>
  );
}

function Join({ code }: { code: string }) {
  const user = useStore(G, s => s.user);
  const [info, setInfo] = useState<{ name: string; gm: string } | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => { api(`/api/invite/${code}`).then(setInfo).catch(e => setErr(e.message)); }, [code]);
  const join = async () => {
    try {
      const r = await api("/api/join", { body: { code } });
      location.hash = `#/g/${r.id}`;
    } catch (e: any) { setErr(e.message); }
  };
  if (err) return <div className="center-screen"><div className="card"><p className="err">{err}</p><a href="#/">Go to Hearth</a></div></div>;
  if (!info) return <div className="center-screen"><div className="spinner" /></div>;
  if (!user) {
    return (
      <div>
        <div className="invite-banner">You're invited to <b>{info.name}</b> (GM: {info.gm}). Create an account or log in to join.</div>
        <Auth invite={code} onDone={join} />
      </div>
    );
  }
  return (
    <div className="center-screen">
      <div className="card">
        <h2>{info.name}</h2>
        <p className="dim">Run by {info.gm}</p>
        <button className="primary big" onClick={join}>Join game</button>
      </div>
    </div>
  );
}

function Lobby() {
  const user = useStore(G, s => s.user)!;
  const [games, setGames] = useState<any[] | null>(null);
  const [err, setErr] = useState("");
  const load = () => api("/api/games").then(r => setGames(r.games)).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);

  const newGame = async () => {
    const name = await prompt("Name your game", "");
    if (!name) return;
    const r = await api("/api/games", { body: { name } });
    location.hash = `#/g/${r.id}`;
  };
  const joinCode = async () => {
    const v = await prompt("Paste an invite link or code");
    if (!v) return;
    const code = v.trim().split("/").pop()!;
    try {
      const r = await api("/api/join", { body: { code } });
      location.hash = `#/g/${r.id}`;
    } catch (e: any) { setErr(e.message); }
  };
  const importGame = async (file: File) => {
    try {
      const data = JSON.parse(await readFileAsText(file));
      const r = await api("/api/games/import", { body: { data } });
      location.hash = `#/g/${r.id}`;
    } catch (e: any) { setErr(e.message || "Import failed"); }
  };
  const logout = async () => {
    await api("/api/logout", { method: "POST" });
    G.set({ user: null });
  };
  return (
    <div className="lobby">
      <header className="lobby-head">
        <h1 className="logo">🔥 Hearth</h1>
        <div className="grow" />
        <span className="dim">Signed in as <b>{user.display}</b></span>
        <button onClick={logout}>Log out</button>
      </header>
      <main className="lobby-main">
        <div className="row gap wrap">
          <button className="primary big" onClick={newGame}>＋ New game</button>
          <button className="big" onClick={joinCode}>Join with invite</button>
          <label className="btn big">
            Import game…
            <input type="file" hidden accept=".json,application/json" onChange={e => e.target.files?.[0] && importGame(e.target.files[0])} />
          </label>
        </div>
        {err && <div className="err">{err}</div>}
        <h2>Your games</h2>
        {!games ? <div className="spinner" /> : games.length === 0 ? (
          <div className="card empty">
            <p>No games yet. Create one to be its GM, or open an invite link from your GM.</p>
          </div>
        ) : (
          <div className="game-grid">
            {games.map(g => (
              <a key={g.id} className="game-card" href={`#/g/${g.id}`}>
                <div className="gc-name">{g.name}</div>
                <div className="dim small">{g.role === "gm" ? "👑 You're the GM" : `GM: ${g.gm}`} · {g.players} member{g.players === 1 ? "" : "s"}</div>
                <div className="dim small">Last played {new Date(g.updated).toLocaleDateString()}</div>
              </a>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export { confirm };
